import { useState, useEffect } from 'react';
import { X, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function UpdatePrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [newVersion, setNewVersion] = useState(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Only track updates AFTER initial load — ignore first install
    let hasController = !!navigator.serviceWorker.controller;
    if (!hasController) return; // First visit, no update possible

    // Listen for messages from service worker
    const onMessage = (event) => {
      if (event.data?.type === 'APP_UPDATED') {
        setNewVersion(event.data.version);
        setShowPrompt(true);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);

    // Listen for new service worker installing
    navigator.serviceWorker.ready.then((registration) => {
      // Only show prompt if a NEW worker installs while we already have one
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          // New worker installed and there's already an active controller
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setShowPrompt(true);
          }
        });
      });

      // Check for updates every 30 minutes
      const interval = setInterval(() => registration.update(), 30 * 60 * 1000);
      return () => clearInterval(interval);
    });

    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  const handleUpdate = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up"
      data-testid="update-prompt"
    >
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-white text-sm">Ny version tillgänglig!</h3>
                <p className="text-white/90 text-xs mt-0.5">
                  {newVersion 
                    ? `Version ${newVersion} finns nu.`
                    : 'Uppdatera för nya funktioner och förbättringar.'
                  }
                </p>
              </div>
              <button onClick={handleDismiss} className="text-white/70 hover:text-white p-1" aria-label="Stäng">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={handleUpdate} size="sm" className="bg-white text-emerald-600 hover:bg-white/90 rounded-full text-xs h-8 px-4" data-testid="update-now-btn">
                <RefreshCw className="w-3 h-3 mr-1.5" />
                Uppdatera nu
              </Button>
              <Button onClick={handleDismiss} variant="ghost" size="sm" className="text-white/90 hover:text-white hover:bg-white/10 rounded-full text-xs h-8 px-3">
                Senare
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
